import React, { useEffect } from 'react';
import Loading from '../../common/components/Loading';
import { Icon, Tag } from '../../common/commonModels';
import ShipmentsTileGroupContainer from './ShipmentsTileGroupContainer';
import TileComponent from '../../common/components/TileComponent';
import UnsortedShipmentsTileComponent from './UnsortedShipmentsTileComponent';
import { InternalShipmentGroup } from '../../models/shipment-group';
import { FeatureToggle } from '../../components/utils/FeatureToggle';
import ShipmentsTileGroupContainerV2 from './ShipmentsTileGroupContainer.v2';
import { Features } from '../../models/features';
import { FlexBox, JustifyContent } from '../../utils/css-classes';
import ShipmentsSummaryComponent from './ShipmentsSummaryComponent';
import { useInternalShipmentGroups } from '../../store/hooks/useInternalShipmentGroups';
import { useHistory } from 'react-router';

const notAvailable = 'N/A';
const widthOffsetForShipmentsTileGroupPx = 282;

// tslint:disable-next-line:function-name
export default function SortationPage() {
  const history = useHistory();
  const {
    internalShipmentGroups,
    getInternalShipmentGroups,
    isInternalShipmentGroupsLoading,
  } = useInternalShipmentGroups();

  useEffect(
    () => {
      getInternalShipmentGroups();
    },
    [],
  );

  if (isInternalShipmentGroupsLoading) {
    return <Loading />;
  }

  const combinedShipmentGroups = new Map<string, InternalShipmentGroup & {tags: Tag[]}>();
  Object.values(internalShipmentGroups).forEach(internalShipmentGroup => {
    if (!combinedShipmentGroups.has(internalShipmentGroup.carrierAccountKey)) {
      combinedShipmentGroups.set(
        internalShipmentGroup.carrierAccountKey,
        {
          ...internalShipmentGroup,
          shipmentCount: 0, // this will be added below
          tags: [],
          palletBoxes: [],
        });
    }
    const combinedShipmentGroup = combinedShipmentGroups.get(internalShipmentGroup.carrierAccountKey)!;
    combinedShipmentGroup.shipmentCount += internalShipmentGroup.shipmentCount;
    combinedShipmentGroup.tags.push({
      key: internalShipmentGroup.tag || 'other',
      value: `${internalShipmentGroup.shipmentCount}`,
    });
    combinedShipmentGroup.palletBoxes.push(...internalShipmentGroup.palletBoxes);
  });

  const carrierAccountRows = [...combinedShipmentGroups.values()]
    .slice()
    .sort((a, b) => a.carrierAccountName.localeCompare(b.carrierAccountName))
    .map(internalShipmentGroup => {
      const totalShipmentCount = internalShipmentGroup.shipmentCount + internalShipmentGroup.palletBoxes.reduce(
        (count, palletBox) => {
          return count + palletBox.shipmentCount;
        },
        0,
      );

      return (
        <div key={internalShipmentGroup.id} className="my-4 card card-minimal">
          <div className={`card-header ${FlexBox.FLEX} ${JustifyContent.SPACE_BETWEEN}`}>
            <span className="h5"> {internalShipmentGroup.carrierAccountName} </span>
            <span className="h5"> {totalShipmentCount} total shipments</span>
          </div>
          <div className="m-2 card-body">
            <div className={FlexBox.FLEX} >
              <UnsortedShipmentsTileComponent
                key={internalShipmentGroup.name}
                shipmentCount={internalShipmentGroup.shipmentCount}
                iconClassName={Icon.Unsorted}
                tags={internalShipmentGroup.tags}
              />

              <span className="mx-4 divider-v" />
              <FeatureToggle whenFlag={Features.EXPERIMENTAL_PB_FLAG} isEnabled={true}>
                <ShipmentsTileGroupContainerV2
                  internalShipmentGroup={internalShipmentGroup}
                  widthOffsetPx={widthOffsetForShipmentsTileGroupPx}
                />
              </FeatureToggle>
              <FeatureToggle whenFlag={Features.EXPERIMENTAL_PB_FLAG} isEnabled={false}>
                <ShipmentsTileGroupContainer
                  internalShipmentGroup={internalShipmentGroup}
                  widthOffsetPx={widthOffsetForShipmentsTileGroupPx}
                />
              </FeatureToggle>
              <div className={`${FlexBox.FLEX} ${JustifyContent.CENTER} ml-auto`}>
                <span className="mx-4 divider-v" />
                <TileComponent
                  title="view trucks"
                  iconClassName={Icon.Truck}
                  onClick={() => history.push('/manifests')}
                />
              </div>
            </div>
          </div>
        </div>
      );
    });

  const totalShipmentCounts: { unsorted: number, boxed: number } = Object.values(internalShipmentGroups).reduce(
    (counts, current) => {
      counts.unsorted += current.shipmentCount;
      counts.boxed += current.palletBoxes.reduce(
        (boxed, palletBox) => {
          return boxed + palletBox.shipmentCount;
        },
        0,
      );

      return counts;
    },
    {
      unsorted: 0,
      boxed: 0,
    },
  );

  return (
    <div>
      <div className="page-title">
        <div className="h1">Carrier Shipments</div>
        <p>See an overview of shipment sortation into pallet boxes</p>
      </div>
      <ShipmentsSummaryComponent
        unsorted={totalShipmentCounts.unsorted || notAvailable}
        boxed={totalShipmentCounts.boxed || notAvailable}
        onTruck={notAvailable}
      />
      {carrierAccountRows}
    </div>
  );
}
